"use client";

import Link from "next/link";

/* ─── Reusable sub-components ──────────────────────────────────────────────── */

function SectionHeading({ n, title }: { n: string; title: string }) {
  return (
    <h2 className="mt-10 mb-4 text-[0.95rem] font-bold tracking-wide text-[#0D1B3E] uppercase">
      {n}. {title}
    </h2>
  );
}

function SubHeading({ id, title }: { id: string; title: string }) {
  return (
    <h3 className="mt-6 mb-2 text-[0.8rem] font-semibold text-[#0D1B3E]">
      {id} {title}
    </h3>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 rounded-md border-l-[3px] border-[#C95A0E] bg-[#FDF8F4] px-4 py-3">
      <p className="m-0 text-[0.72rem] leading-relaxed text-[#5A3E2B] italic">
        {children}
      </p>
    </div>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export default function TermsOfServicePage() {
  return (
    <main className="bg-white font-['DM_Sans',sans-serif] text-[#333]">
      {/* ── Hero ── */}
      <section className="bg-[#0D1B3E] py-16 text-center text-white">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-[0.6rem] font-bold uppercase tracking-[0.25em] text-[#C95A0E]">
            Legal
          </p>
          <h1 className="mt-2 font-['Cormorant_Garamond',Georgia,serif] text-[1.75rem] font-semibold leading-tight sm:text-[2.25rem]">
            Terms of Service
          </h1>
          <p className="mt-1 font-['Cormorant_Garamond',Georgia,serif] text-[1rem] text-white/70">
            Terms &amp; Conditions of Use and Engagement
          </p>
          <p className="mt-2 text-[0.72rem] text-white/50">
            Effective: December 2024 &nbsp;|&nbsp; Last Updated: March 2026
          </p>
          <p className="mt-1 text-[0.65rem] text-white/40">
            Applicable to all services offered by Farsight Solutions LLP
          </p>
        </div>
      </section>

      {/* ── Key badges ── */}
      <div className="mx-auto -mt-6 grid max-w-3xl grid-cols-1 gap-3 px-6 sm:grid-cols-3">
        {[
          { label: "Governing Law", value: "India — Courts in Noida, UP" },
          { label: "Dispute Resolution", value: "Negotiation → Arbitration → Litigation" },
          { label: "Confidentiality", value: "3-year post-termination obligation" },
        ].map((b) => (
          <div
            key={b.label}
            className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center shadow-sm"
          >
            <p className="text-[0.6rem] font-bold uppercase tracking-[0.15em] text-[#C95A0E]">
              {b.label}
            </p>
            <p className="mt-1 text-[0.7rem] font-medium leading-snug text-[#0D1B3E]">
              {b.value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Body ── */}
      <article className="prose-none mx-auto max-w-3xl px-6 pb-20 pt-10 text-[0.75rem] leading-[1.85] text-[#444]">

        {/* 1 ─ Introduction & Acceptance */}
        <SectionHeading n="1" title="Introduction & Acceptance of Terms" />
        <p>
          Welcome, and thank you for choosing us. These Terms of Service (&ldquo;Terms&rdquo;, &ldquo;Agreement&rdquo;) govern your access to and use of all services, platforms, and solutions offered by Farsight Solutions LLP (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;the Company&rdquo;), a company registered under the laws of India.
        </p>
        <p>
          By accessing our website, booking a consultation, engaging our services, signing a service agreement, making a payment, or simply using our platform in any form, you confirm that you have read, understood, and agree to be bound by these Terms. This agreement is binding and constitutes a valid contract under the Indian Contract Act, 1872.
        </p>
        <p>
          If you are representing a business, partnership, LLP, private limited company, or any other legal entity, you represent that you have the authority to bind that entity to these Terms.
        </p>
        <Callout>
          We encourage you to read through these Terms carefully. Our goal is to work with you as a trusted partner, and these Terms exist to set clear, fair expectations for both of us. If you have questions, our team is always happy to help.
        </Callout>

        {/* 2 ─ About the Company */}
        <SectionHeading n="2" title="About the Company & Our Multi-Service Nature" />
        <p>
          Farsight Solutions LLP is a full-spectrum business solutions company based in India. We bring together expertise across multiple domains to serve entrepreneurs, startups, MSMEs, mid-size businesses, and corporates at every stage of their journey.
        </p>
        <p className="mt-2 font-semibold text-[#0D1B3E]">Our core service verticals include:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Business Consultancy</strong> — Strategic guidance, growth advisory, and 360° business support</li>
          <li><strong>Funding &amp; Financial Services</strong> — Funding program facilitation, government grant advisory, NBFC and loan assistance</li>
          <li><strong>Certification &amp; Registrations</strong> — ISO, Startup India, Udyam, GeM, ZED, Section 80 IAC, FCRA, 12A &amp; 80G</li>
          <li><strong>Legal Services</strong> — Contracts &amp; agreements drafting, corporate governance advisory</li>
          <li><strong>Marketing Services</strong> — Brand building and performance marketing</li>
          <li><strong>IT &amp; Technology Solutions</strong> — Software, app, web development, cloud, CRM/ERP, AI/ML, cybersecurity, and DevOps</li>
        </ul>
        <p className="mt-3">
          Because we operate across multiple domains, the nature, process, timelines, and outcomes of each service are distinct. The specific details governing each service vertical are explained in Section 4 (Scope of Services) and the business-specific clauses throughout this document.
        </p>

        {/* 3 ─ Eligibility */}
        <SectionHeading n="3" title="Eligibility of Users" />
        <p>To engage our services, you must meet the following eligibility criteria:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>You must be at least 18 years of age.</li>
          <li>You must be legally competent to enter into binding contracts under the Indian Contract Act, 1872.</li>
          <li>If engaging on behalf of a business entity, you must have proper authorisation to represent that entity.</li>
          <li>You must not be under any legal restriction that would prevent you from entering this Agreement.</li>
          <li>You must provide accurate, complete, and truthful information throughout your engagement with us.</li>
        </ul>
        <p className="mt-3">
          By using our services, you represent and warrant that you meet all the above criteria. We reserve the right to decline or discontinue services if eligibility criteria are not met.
        </p>

        {/* 4 ─ Detailed Scope of Services */}
        <SectionHeading n="4" title="Detailed Scope of Services" />

        <SubHeading id="4.1" title="Business Consultancy" />
        <p>Our consultancy services are designed to help you build clarity, direction, and momentum for your business. We offer:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Strategic Overview:</strong> Comprehensive business assessments, market analysis, and goal-setting frameworks tailored to your industry.</li>
          <li><strong>Growth Advisory:</strong> Actionable roadmaps for scaling operations, entering new markets, and optimising revenue streams.</li>
          <li><strong>360° Business Support:</strong> Ongoing advisory support covering operations, finance, sales, HR, and technology.</li>
        </ul>
        <p className="mt-2">
          Consultancy engagements are time- and scope-based. The output of consultancy services is professional advice, analysis, and recommendations. Implementation of those recommendations remains the client&apos;s responsibility unless a separate implementation agreement is signed.
        </p>

        <SubHeading id="4.2" title="Funding & Financial Services" />
        <p>We assist businesses in identifying, preparing, and applying for suitable funding opportunities. Our services include:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Funding Programs:</strong> Identification of suitable equity, debt, or grant-based funding options based on your profile.</li>
          <li><strong>Government Grants:</strong> Advisory and application support for central and state government schemes.</li>
          <li><strong>NBFC &amp; Loan Assistance:</strong> Guidance on documentation, eligibility assessment, and liaising with lending institutions.</li>
        </ul>
        <Callout>
          We act as a knowledgeable facilitator in your funding journey. The ultimate approval or disbursement of funds is decided entirely by banks, NBFCs, government bodies, or other financial institutions, and is not within our control. We enhance your chances of success by presenting the strongest possible application based on your profile, documentation, and eligibility.
        </Callout>

        <SubHeading id="4.3" title="Certification & Registrations" />
        <p>We provide expert advisory and application support for a range of certifications and registrations:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>ISO Certification</li>
          <li>Startup India Recognition</li>
          <li>Udyam Registration (MSME)</li>
          <li>GeM (Government e-Marketplace) Registration</li>
          <li>ZED (Zero Defect Zero Effect) Certification</li>
          <li>Section 80 IAC Tax Exemption</li>
          <li>FCRA Registration</li>
          <li>12A &amp; 80G Certification</li>
        </ul>
        <p className="mt-2">
          Our team prepares, reviews, and submits the required documentation on your behalf where permitted. The final approval, timeline, and validity of any certification or registration is determined solely by the relevant government department, ministry, or accreditation body. We commit to doing everything within our scope to strengthen your application, but processing timelines and final decisions are outside our control.
        </p>

        <SubHeading id="4.4" title="Legal Services" />
        <p>Our legal service offerings include:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Contracts &amp; Agreements:</strong> Drafting, reviewing, and structuring commercial contracts, vendor agreements, NDAs, employment agreements, partnership deeds, and more.</li>
          <li><strong>Corporate Governance:</strong> Advisory on board structure, compliance frameworks, statutory obligations, and governance best practices.</li>
        </ul>
        <Callout>
          Important: Our legal services are advisory and documentation-based in nature. Unless explicitly and separately agreed upon in writing, our services do not include representation before courts, tribunals, or adjudicating authorities. For litigation or court representation, you should engage a licensed advocate.
        </Callout>

        <SubHeading id="4.5" title="Marketing Services" />
        <p>We help businesses build visibility, credibility, and customer traction through:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Brand Building:</strong> Visual identity, brand strategy, messaging frameworks, and positioning.</li>
          <li><strong>Performance Marketing:</strong> Digital advertising campaigns across Google, Meta, and other relevant platforms.</li>
        </ul>
        <p className="mt-2">
          Marketing outcomes are influenced by market conditions, audience behaviour, platform algorithms, competitive activity, and your own product-market fit. We bring our best expertise, data, and creative thinking to every campaign, and we are committed to continuously optimising for the strongest possible results. While we cannot guarantee specific metrics like ROI, lead volumes, or conversion rates, we are fully accountable to our process, creativity, and performance reporting.
        </p>

        <SubHeading id="4.6" title="IT & Technology Solutions" />
        <p>Our technology team delivers end-to-end digital solutions including:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Software &amp; App Development</li>
          <li>Web Development</li>
          <li>Cloud Services &amp; Infrastructure</li>
          <li>CRM &amp; ERP Implementations</li>
          <li>AI/ML Solutions</li>
          <li>Cybersecurity Services</li>
          <li>DevOps &amp; CI/CD Solutions</li>
        </ul>
        <p className="mt-2">
          All technology projects are executed based on a mutually agreed scope of work, project brief, or Statement of Work (SoW). Deliverables, timelines, and milestones are defined at project initiation. Changes to the agreed scope (change requests) will be assessed separately and may involve additional time and cost. Incomplete or unclear requirements provided by the client may affect delivery timelines, for which the Company shall not be held responsible.
        </p>

        {/* 5 ─ How Our Services Work */}
        <SectionHeading n="5" title="How Our Services Work" />
        <p>We believe in complete transparency about how we engage with you. Here&apos;s what you can expect:</p>

        <div className="mt-4 space-y-4">
          {[
            { step: "Step 1", title: "Discovery & Needs Assessment", desc: "Every engagement begins with a conversation. We understand your goals, challenges, and requirements. This may be a free discovery call or a paid consultation depending on the service." },
            { step: "Step 2", title: "Proposal & Agreement", desc: "We present a clear proposal outlining the services, scope, timelines, deliverables, and commercial terms. Work begins only after mutual agreement and the applicable payment terms are fulfilled." },
            { step: "Step 3", title: "Execution & Communication", desc: "Our team works diligently on your engagement. We keep you informed at agreed intervals. For services involving external authorities (government portals, banks, certification bodies), we coordinate and update you on progress." },
            { step: "Step 4", title: "Delivery & Review", desc: "Completed work is delivered per agreed terms. For ongoing services, regular reviews are conducted to ensure alignment with your goals." },
            { step: "Step 5", title: "Follow-up & Support", desc: "Post-delivery support is provided as per your service plan. Additional support requirements beyond the original scope can be arranged as a new engagement." },
          ].map((s) => (
            <div key={s.step} className="flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0D1B3E] text-[0.6rem] font-bold text-white">
                {s.step.split(" ")[1]}
              </span>
              <div>
                <p className="font-semibold text-[#0D1B3E]">{s.title}</p>
                <p className="mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 6 ─ Client Responsibilities */}
        <SectionHeading n="6" title="Client Responsibilities" />
        <p>A successful engagement is always a two-way partnership. As our client, your active participation and cooperation are essential. You agree to the following responsibilities:</p>

        <SubHeading id="6.1" title="Accuracy of Information" />
        <p>
          You shall provide accurate, complete, current, and truthful information at all stages of the engagement. Any misrepresentation, omission, or inaccuracy in information provided by you that adversely affects our work or the outcome of an application shall be your sole responsibility.
        </p>

        <SubHeading id="6.2" title="Timely Document Submission" />
        <p>
          Many of our services—especially registrations, certifications, and funding applications—require timely submission of documents. Delays caused by late or incomplete document submission from your end may impact timelines, and such delays shall not be attributable to the Company.
        </p>

        <SubHeading id="6.3" title="Active Cooperation" />
        <p>
          You agree to be reasonably available for communication, reviews, approvals, and feedback during the course of your engagement. Prolonged non-responsiveness that hampers delivery shall not constitute a basis for refund or claims against the Company.
        </p>

        <SubHeading id="6.4" title="Compliance with Laws" />
        <p>
          You shall ensure that your business activities, the information you provide, and your reasons for seeking our services are lawful under applicable Indian and international laws. We reserve the right to discontinue services if we discover any illegal or unethical purpose.
        </p>

        <SubHeading id="6.5" title="Authorised Representatives" />
        <p>
          If you designate a representative or point of contact for the engagement, you confirm that such individual is authorised to provide instructions, approvals, and information on your behalf. The Company shall not be liable for consequences arising from unauthorised or unclear instructions.
        </p>

        {/* 7 ─ Fees, Payments & Refund Terms */}
        <SectionHeading n="7" title="Fees, Payments & Refund Terms" />

        <SubHeading id="7.1" title="Fee Structure" />
        <p>
          Our service fees are communicated clearly in the proposal or service agreement prior to engagement. Fees may be structured as a one-time fixed fee, milestone-based payments, monthly retainers, or a combination, depending on the nature of the service.
        </p>

        <SubHeading id="7.2" title="Payment Terms" />
        <p>
          Payments are due as per the schedule agreed in the service proposal or invoice. We reserve the right to pause or suspend services in the event of overdue payments. All payments are to be made in Indian Rupees (INR) unless separately agreed in writing.
        </p>
        <p>Invoices raised are subject to applicable Goods and Services Tax (GST) as per prevailing Indian tax laws.</p>

        <SubHeading id="7.3" title="Work-Based Billing" />
        <p>
          Our billing reflects the work actually performed by our team. In cases where work has already commenced—including but not limited to document preparation, application drafting, strategy sessions, design work, or code development—the corresponding portion of the fees is considered earned and is not refundable.
        </p>

        <SubHeading id="7.4" title="Refund Policy" />
        <p>We take our commitments seriously and strive to deliver exceptional value. Our refund terms are designed to be fair to both parties:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>If a service has not been initiated and no work has commenced, you may request a cancellation and refund within 48 hours of payment, subject to a processing fee of up to 10%.</li>
          <li>Where work has commenced, refunds (partial or full) will be assessed on a case-by-case basis, taking into account the work completed, resources deployed, and the reason for the refund request.</li>
          <li>Refunds will <strong>NOT</strong> be issued on the basis of outcomes dependent on third-party authorities (e.g., loan rejection by a bank, rejection of a government registration, failure to meet certification standards), as the Company&apos;s scope is limited to preparation and facilitation.</li>
          <li>Retainer fees are non-refundable once the retainer period has commenced.</li>
          <li>Technology project fees paid for completed milestones are non-refundable.</li>
          <li>Approved and processed refunds will be credited to the original payment source within 7–14 working days.</li>
        </ul>
        <Callout>
          If you feel your situation warrants a review, please write to us at accounts@farsight.co.in. We are committed to resolving concerns fairly and promptly.
        </Callout>

        {/* 8 ─ Understanding Service Outcomes */}
        <SectionHeading n="8" title="Understanding Service Outcomes" />
        <p>
          We want to be genuinely useful to you, and part of that is being honest about how outcomes work in the real world.
        </p>
        <p>
          In consultancy, funding, certification, legal, and marketing services, results are shaped by a combination of factors—many of which are external to any service provider. These include your business&apos;s eligibility and financial health, government policy at the time of application, the decisions of regulatory or financial authorities, market conditions, and your own execution of recommendations.
        </p>
        <p>
          What we do is bring our full expertise, experience, and best practices to every engagement, significantly enhancing your probability of a successful outcome. Think of us as highly skilled co-pilots: we navigate the best possible route, prepare the strongest application or strategy, and position you for success. But external conditions and decision-makers are beyond any consultant&apos;s control.
        </p>
        <Callout>
          We will always communicate honestly with you about where your application stands, flag any risks we see, and recommend the best course of action. Our reputation is built on doing right by our clients, and we take that seriously.
        </Callout>

        {/* 9 ─ Third-Party Services */}
        <SectionHeading n="9" title="Third-Party Services & Disclaimer" />
        <p>Several of our services involve coordination with, or submission to, external third parties including:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Banks, NBFCs, and financial institutions</li>
          <li>Central and state government ministries and portals</li>
          <li>ISO and quality certification bodies</li>
          <li>Digital advertising platforms (Google, Meta, etc.)</li>
          <li>Domain registrars, hosting providers, and cloud service providers</li>
          <li>Legal and regulatory authorities</li>
        </ul>
        <p className="mt-3">
          The Company acts exclusively as an advisor, consultant, and facilitator in all such interactions. We do not control, direct, or influence the internal policies, processing timelines, approval criteria, or decisions of these third-party organisations.
        </p>
        <p>
          We are not responsible for delays, rejections, policy changes, system outages, or any other actions or inactions of third parties that may affect the delivery or outcome of our services. However, we will always advocate for your best interests and explore every available avenue within our scope.
        </p>

        {/* 10 ─ Communication Consent */}
        <SectionHeading n="10" title="Communication Consent" />
        <p>
          By engaging with us—whether through our website, inquiry forms, service bookings, or direct contact—you expressly consent to being contacted by Farsight Solutions LLP through the following channels:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Phone calls (including mobile)</li>
          <li>WhatsApp messages</li>
          <li>Email communications</li>
          <li>SMS notifications</li>
        </ul>
        <p className="mt-3">
          This consent covers communications related to your service inquiry, project updates, invoices, promotional offers, new service announcements, and relevant business information.
        </p>
        <p>
          If you submit an inquiry through our platform for lead generation purposes, or register for any of our programmes, you agree to be contacted by our team for follow-up and advisory purposes, even if your number is registered on the Do Not Disturb (DND) or National Customer Preference Registry (NCPR).
        </p>
        <p>
          You may opt out of promotional communications at any time by writing to us at{" "}
          <a href="mailto:privacy@farsight.co.in" className="text-[#1565C0] underline">privacy@farsight.co.in</a>.
          Transactional and service-related communications will continue as necessary for your active engagement.
        </p>

        {/* 11 ─ Confidentiality & Data Handling */}
        <SectionHeading n="11" title="Confidentiality & Data Handling" />

        <SubHeading id="11.1" title="Confidentiality" />
        <p>
          Both parties acknowledge that during the course of this engagement, each may receive or have access to confidential information belonging to the other party. Confidential information includes, but is not limited to, business plans, financial data, client lists, technical specifications, strategies, and any information marked as confidential or that a reasonable person would understand to be confidential.
        </p>
        <p className="mt-2">Both parties agree to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Keep all confidential information strictly private and protected.</li>
          <li>Not disclose such information to any third party without prior written consent.</li>
          <li>Use confidential information solely for the purpose of fulfilling the engagement.</li>
        </ul>
        <p className="mt-2">
          This confidentiality obligation survives the termination of this Agreement for a period of <strong>three (3) years</strong>.
        </p>

        <SubHeading id="11.2" title="Data Protection" />
        <p>
          We are committed to protecting your personal and business data in compliance with the Digital Personal Data Protection Act, 2023 (DPDPA) and the Information Technology Act, 2000.
        </p>
        <p>
          We collect only the data necessary to deliver our services, and we use it solely for those purposes. We do not sell your personal data to third parties. Data is stored securely and accessed only by authorised personnel involved in your engagement.
        </p>
        <p>
          You have the right to access, correct, or request deletion of your personal data by writing to us at{" "}
          <a href="mailto:privacy@farsight.co.in" className="text-[#1565C0] underline">privacy@farsight.co.in</a>.
          For full details on how we handle your data, please refer to our{" "}
          <Link href="/privacy" className="text-[#1565C0] underline">Privacy Policy</Link>.
        </p>

        {/* 12 ─ Intellectual Property */}
        <SectionHeading n="12" title="Intellectual Property" />

        <SubHeading id="12.1" title="Company Intellectual Property" />
        <p>
          All proprietary methodologies, frameworks, templates, consulting tools, strategic models, software code, marketing assets, design elements, and platform components developed or owned by Farsight Solutions LLP remain the exclusive intellectual property of the Company. Nothing in this Agreement transfers any ownership of such IP to the client.
        </p>

        <SubHeading id="12.2" title="Deliverables" />
        <p>
          Upon full payment of all applicable fees, the client receives a licence to use the specific deliverables created for them under the engagement (e.g., website, software, marketing creatives, drafted contracts). Underlying proprietary frameworks, code libraries, or toolsets developed by the Company remain the Company&apos;s property.
        </p>
        <p>
          For technology projects, unless a specific work-for-hire clause is included in the separate project agreement, source code and underlying architecture developed using the Company&apos;s proprietary components remain licenced, not assigned.
        </p>

        <SubHeading id="12.3" title="Client Content" />
        <p>
          Content, materials, logos, brand assets, and data provided by the client remain the client&apos;s property. By sharing them with us, you grant a limited, non-exclusive licence to use them solely for the purpose of delivering the agreed services.
        </p>

        {/* 13 ─ Limitation of Liability */}
        <SectionHeading n="13" title="Limitation of Liability" />
        <p>
          We stand behind our work and are committed to delivering genuine value. At the same time, it is important to set realistic and fair expectations regarding liability.
        </p>
        <p>To the fullest extent permitted by applicable law:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            The Company&apos;s total aggregate liability arising out of or in connection with any service engagement shall not exceed the total fees actually paid by the client for the specific service giving rise to the claim, within the <strong>twelve (12) month period</strong> preceding the claim.
          </li>
          <li>
            The Company shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, loss of business opportunity, loss of data, reputational harm, or any other indirect losses, even if the Company has been advised of the possibility of such damages.
          </li>
          <li>
            The Company shall not be liable for delays, rejections, or adverse outcomes arising from the decisions of third-party authorities including banks, government bodies, certification agencies, or courts.
          </li>
          <li>
            The Company shall not be liable for any loss arising from client-supplied inaccurate or incomplete information, or from the client&apos;s failure to cooperate or respond in a timely manner.
          </li>
        </ul>
        <p className="mt-2">
          These limitations apply regardless of the form of action, whether in contract, tort (including negligence), or otherwise.
        </p>

        {/* 14 ─ Indemnification */}
        <SectionHeading n="14" title="Indemnification" />
        <p>
          You agree to defend, indemnify, and hold harmless Farsight Solutions LLP, its directors, officers, employees, consultants, and agents from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising from or related to:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Your breach of any provision of these Terms.</li>
          <li>Any inaccurate, incomplete, or misleading information provided by you.</li>
          <li>Your violation of any applicable law, regulation, or third-party right.</li>
          <li>Any misuse of our services or platform.</li>
          <li>Any third-party claims arising from your use of the deliverables we provide.</li>
        </ul>

        {/* 15 ─ Prohibited Activities */}
        <SectionHeading n="15" title="Prohibited Activities" />
        <p>We maintain high standards of integrity across all our engagements. You agree <strong>NOT</strong> to use our services, platform, or team for any of the following:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Providing false or fraudulent information to us or to any third-party authority for the purpose of obtaining a registration, certification, funding, or approval.</li>
          <li>Misrepresenting your business, its financials, ownership, or eligibility for any programme or scheme.</li>
          <li>Using our legal or IT services to draft documents or build systems intended for unlawful purposes.</li>
          <li>Attempting to reverse-engineer, copy, or reproduce our proprietary methodologies, frameworks, or software.</li>
          <li>Using our platform or communications for spam, phishing, or any form of harassment.</li>
          <li>Circumventing or attempting to circumvent any security measures or access controls on our platform.</li>
          <li>Engaging in any activity that violates applicable Indian or international laws.</li>
        </ul>
        <Callout>
          Any engagement found to involve prohibited activities will be immediately terminated. The Company reserves the right to report such activities to appropriate legal authorities and to pursue recovery of damages.
        </Callout>

        {/* 16 ─ Service Suspension & Termination */}
        <SectionHeading n="16" title="Service Suspension & Termination" />

        <SubHeading id="16.1" title="Termination by Client" />
        <p>
          You may terminate an engagement by providing written notice to us. Termination does not entitle you to a refund of fees already earned by the Company for work performed up to the termination date. Any outstanding invoices remain payable.
        </p>

        <SubHeading id="16.2" title="Termination by Company" />
        <p>We reserve the right to suspend or terminate services with reasonable notice under the following circumstances:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Non-payment or significant overdue payments.</li>
          <li>Breach of any material provision of these Terms.</li>
          <li>Discovery of misrepresentation or fraud.</li>
          <li>Persistent non-cooperation that makes service delivery impossible.</li>
          <li>Any activity that exposes the Company to legal, reputational, or financial risk.</li>
        </ul>

        <SubHeading id="16.3" title="Effect of Termination" />
        <p>
          Upon termination, all licences granted under this Agreement cease, outstanding fees become immediately due and payable, and each party shall return or destroy confidential information of the other party as requested. Provisions that by their nature should survive termination (including confidentiality, IP ownership, limitation of liability, and indemnification) shall continue to apply.
        </p>

        {/* 17 ─ External Factors & Delays */}
        <SectionHeading n="17" title="External Factors & Delays" />
        <p>We want to be upfront: many of the services we offer involve processes that are not entirely within any consultant&apos;s hands. You acknowledge and accept that the following factors may affect delivery timelines and outcomes:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Changes in government policies, budget allocations, or scheme guidelines (especially for funding and certification services).</li>
          <li>Technical issues with government portals, banking systems, or third-party platforms.</li>
          <li>Delays in processing by banks, NBFCs, regulatory bodies, or certification authorities.</li>
          <li>Your own delay in providing required documents, approvals, or feedback.</li>
          <li>Revisions to the scope of services at your request.</li>
        </ul>
        <p className="mt-3">
          In all such cases, we will proactively communicate with you, explore alternative approaches where possible, and update our timelines accordingly. Delays arising from these external factors do not constitute a breach on the part of the Company.
        </p>

        {/* 18 ─ Force Majeure */}
        <SectionHeading n="18" title="Force Majeure" />
        <p>
          The Company shall not be held liable for any failure or delay in performance of its obligations under this Agreement to the extent such failure or delay is caused by events beyond the Company&apos;s reasonable control, including but not limited to:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Natural disasters, floods, earthquakes, or other acts of God</li>
          <li>Pandemics, epidemics, or government-declared public health emergencies</li>
          <li>War, terrorism, civil unrest, or government action</li>
          <li>Widespread internet or telecom outages</li>
          <li>Regulatory or legislative changes that materially affect service delivery</li>
        </ul>
        <p className="mt-3">
          In such events, the Company will notify you as soon as reasonably practicable and work with you to resume services at the earliest opportunity. If a force majeure event continues for more than <strong>60 days</strong>, either party may terminate the affected engagement without liability for the period of non-performance.
        </p>

        {/* 19 ─ Updates to Terms */}
        <SectionHeading n="19" title="Updates to Terms" />
        <p>
          We may update or revise these Terms from time to time to reflect changes in our services, applicable laws, or business practices. When we make material changes, we will notify you via email or prominent notice on our website at least <strong>15 days</strong> before the changes take effect.
        </p>
        <p>
          Continued use of our services after the effective date of any revision constitutes your acceptance of the updated Terms. If you do not agree with any changes, you may terminate your engagement as described in Section 16.
        </p>
        <p>
          We recommend bookmarking this page and reviewing it periodically. The &ldquo;Last Updated&rdquo; date at the top of this document reflects when the most recent revision was made.
        </p>

        {/* 20 ─ Governing Law & Jurisdiction */}
        <SectionHeading n="20" title="Governing Law & Jurisdiction" />
        <p>
          These Terms and any dispute or claim arising out of or in connection with them (including non-contractual disputes or claims) shall be governed by and construed in accordance with the <strong>laws of India</strong>, without regard to its conflict of law principles.
        </p>
        <p>
          The parties agree that the courts located in <strong>Noida, Uttar Pradesh, India</strong> shall have exclusive jurisdiction over any disputes arising out of or relating to this Agreement. By engaging our services, you submit to the personal jurisdiction of these courts.
        </p>
        <p>
          Before initiating formal legal proceedings, both parties agree to make a genuine attempt to resolve disputes through good-faith negotiation for a period of <strong>30 days</strong> from the date of written notice of the dispute. If unresolved, the parties may agree to pursue arbitration under the Arbitration and Conciliation Act, 1996, before resorting to litigation.
        </p>

        {/* 21 ─ Business-Specific Clauses */}
        <SectionHeading n="21" title="Business-Specific Clauses" />

        <SubHeading id="21.1" title="Funding & Loan Services" />
        <p>
          Funding facilitation services involve preparing and optimising your application materials, assessing your eligibility, and liaising with relevant financial institutions. The final decision on whether to sanction, approve, or disburse a loan or grant rests entirely with the financial institution or government authority involved.
        </p>
        <p>
          The Company does not guarantee sanction, disbursement, or approval of any loan, grant, or funding. Our commitment is to present the most compelling application possible on your behalf. Rejections by financial authorities do not constitute failure of service delivery by the Company.
        </p>

        <SubHeading id="21.2" title="Certification & Registrations" />
        <p>
          We prepare and file the required documentation with the highest level of accuracy and completeness. Government portals, certification bodies, and accreditation agencies set their own processing timelines, which may vary from weeks to several months. We cannot accelerate these timelines beyond what is within our control.
        </p>
        <p>
          In the event of a rejection due to policy non-compliance on your part, or due to reasons not attributable to the preparation of your application by us, refunds for the work performed will not be applicable. We will advise on the path forward and assist with rectification where possible.
        </p>

        <SubHeading id="21.3" title="Legal Services" />
        <p>
          All legal advisory services provided by the Company are professional advisory opinions and should be treated as such. They do not constitute legal representation in courts, tribunals, or before quasi-judicial authorities unless explicitly agreed in a separate retainer or engagement letter signed by both parties.
        </p>
        <p>
          We strongly recommend seeking the advice of a qualified licensed advocate for any matter that may proceed to litigation.
        </p>

        <SubHeading id="21.4" title="Marketing Services" />
        <p>
          Digital marketing outcomes are influenced by platform algorithms, market competition, audience behaviour, and the quality of the underlying product or service being marketed. Our team commits to bringing industry-leading strategy, creative excellence, and data-driven optimisation to every campaign.
        </p>
        <p>
          We do not guarantee specific results in terms of leads generated, cost per acquisition, ROAS, conversion rates, or follower growth. We do guarantee our full effort, transparent reporting, and continuous improvement throughout the engagement.
        </p>

        <SubHeading id="21.5" title="IT & Technology Services" />
        <p>
          All technology project deliverables are based on the scope agreed at project initiation. Changes requested after scope finalisation—whether additions, modifications, or redesigns—will be handled as formal change requests and may involve additional time and cost.
        </p>
        <p>
          The client is responsible for providing timely, clear, and accurate requirements. Ambiguity or changes in requirements mid-project may impact delivery timelines. The Company is not responsible for delays arising from unclear or evolving client requirements.
        </p>
        <p>
          Post-delivery bug fixes for issues arising from agreed specifications will be addressed within the warranty period stated in the project agreement. Issues arising from client modifications, third-party integrations not within scope, or environmental factors outside the Company&apos;s control may be handled as a new service engagement.
        </p>

        <SubHeading id="21.6" title="Consultation Services" />
        <p>
          Consultations (whether free discovery calls or paid advisory sessions) are meant to explore possibilities, assess fit, and provide initial guidance. Outcomes of consultations are not binding commitments. No specific result, approval, or guarantee is promised during a consultation session unless confirmed in a formal service agreement.
        </p>

        <SubHeading id="21.7" title="Government Schemes" />
        <p>
          All advisory and application services related to government schemes, grants, and programmes are subject to the prevailing policies of the relevant central or state government authority at the time of application. The Company shall not be liable for changes in scheme eligibility, funding availability, deadlines, or discontinuation of schemes after the commencement of our engagement.
        </p>

        {/* 22 ─ Contact Information */}
        <SectionHeading n="22" title="Contact Information" />
        <p>We&apos;re here to help. If you have any questions, concerns, or feedback regarding these Terms of Service, or any of our services, please reach out to us:</p>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-[0.7rem]">
            <tbody className="divide-y divide-gray-200">
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Company Name</td><td className="py-2">Farsight Solutions LLP</td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Registered Address</td><td className="py-2">ILD Business Center, H-207, Sector 63, Noida, Uttar Pradesh – 201009, India</td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">General Enquiries</td><td className="py-2"><a href="mailto:info@farsight.co.in" className="text-[#1565C0] underline">info@farsight.co.in</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Legal &amp; Compliance</td><td className="py-2"><a href="mailto:legal@farsight.co.in" className="text-[#1565C0] underline">legal@farsight.co.in</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Data &amp; Privacy</td><td className="py-2"><a href="mailto:privacy@farsight.co.in" className="text-[#1565C0] underline">privacy@farsight.co.in</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Accounts &amp; Billing</td><td className="py-2"><a href="mailto:accounts@farsight.co.in" className="text-[#1565C0] underline">accounts@farsight.co.in</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Phone</td><td className="py-2"><a href="tel:+919821605930" className="text-[#1565C0] underline">+91 9821605930</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Website</td><td className="py-2"><a href="https://www.farsight.co.in" className="text-[#1565C0] underline" target="_blank" rel="noopener noreferrer">www.farsight.co.in</a></td></tr>
              <tr><td className="py-2 pr-4 font-semibold text-[#0D1B3E] whitespace-nowrap">Business Hours</td><td className="py-2">Monday to Saturday, 10:00 AM – 6:00 PM IST</td></tr>
            </tbody>
          </table>
        </div>

        {/* ── Closing ── */}
        <div className="mt-12 rounded-lg border border-[#C95A0E]/20 bg-[#FDF8F4] px-6 py-6 text-center">
          <p className="text-[0.85rem] font-semibold text-[#0D1B3E]">
            Thank you for trusting Farsight Solutions
          </p>
          <p className="mt-2 text-[0.72rem] text-[#5A3E2B]">
            We look forward to being a meaningful part of your growth journey. These Terms exist to protect both of us and to ensure we work together with clarity, fairness, and mutual respect.
          </p>
        </div>

        <p className="mt-8 text-center text-[0.65rem] text-gray-400">
          © 2026 Farsight Solutions LLP. All Rights Reserved.
        </p>
      </article>
    </main>
  );
}
